import { useEffect, useRef, useState, type FormEvent } from 'react';

import type { BusinessServiceRecord } from '../api/schemas';
import { formatMoneyCents } from '../lib/format';
import { parsePriceInput } from '../lib/money';
import { ErrorBanner } from './ErrorBanner';
import { FormField } from './FormField';

/** Digits 1..9 add the nth Servicio without leaving the keyboard. */
const MAX_KEYBOARD_TILES = 9;

type ChargeCatalogGridProps = {
  services: BusinessServiceRecord[];
  catalogLabel: string;
  isLoading: boolean;
  isError: boolean;
  errorMessage?: string;
  onAddService: (service: BusinessServiceRecord) => void;
  onAddFreeform: (name: string, unitPriceCents: number) => void;
};

function isTypingTarget(target: EventTarget | null): boolean {
  if (!(target instanceof HTMLElement)) return false;
  return ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName);
}

/** The Servicio grid (tap adds a Línea, tap again increments qty) plus "Otro artículo". */
export function ChargeCatalogGrid({
  services,
  catalogLabel,
  isLoading,
  isError,
  errorMessage,
  onAddService,
  onAddFreeform,
}: ChargeCatalogGridProps) {
  const [freeformOpen, setFreeformOpen] = useState(false);
  const [name, setName] = useState('');
  const [priceInput, setPriceInput] = useState('');
  const [error, setError] = useState<string | undefined>();

  const servicesRef = useRef(services);
  servicesRef.current = services;
  const addRef = useRef(onAddService);
  addRef.current = onAddService;

  useEffect(() => {
    function handleKeyDown(event: KeyboardEvent) {
      if (event.metaKey || event.ctrlKey || event.altKey) return;
      if (isTypingTarget(event.target)) return;
      const digit = Number(event.key);
      if (!Number.isInteger(digit) || digit < 1 || digit > MAX_KEYBOARD_TILES) return;
      const service = servicesRef.current[digit - 1];
      if (!service) return;
      event.preventDefault();
      addRef.current(service);
    }
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  function handleFreeformSubmit(event: FormEvent) {
    event.preventDefault();
    const trimmedName = name.trim();
    if (trimmedName === '') {
      setError('Escribe un nombre para el artículo.');
      return;
    }
    const price = parsePriceInput(priceInput);
    if (!price.ok) {
      setError(price.message);
      return;
    }
    onAddFreeform(trimmedName, price.cents);
    setName('');
    setPriceInput('');
    setError(undefined);
    setFreeformOpen(false);
  }

  if (isError) {
    return <ErrorBanner message={errorMessage ?? `No pudimos cargar ${catalogLabel.toLowerCase()}.`} />;
  }

  if (isLoading) {
    return (
      <div className="pos-catalog" aria-hidden="true">
        {Array.from({ length: 4 }, (_, index) => (
          <div key={index} className="pos-tile skeleton">
            <span className="bone" style={{ width: '70%' }} />
            <span className="bone" style={{ width: '40%' }} />
          </div>
        ))}
      </div>
    );
  }

  return (
    <div className="pos-catalog" aria-label={catalogLabel}>
      {services.map((service, index) => (
        <button
          key={service.id}
          type="button"
          className="pos-tile"
          onClick={() => onAddService(service)}
        >
          <span className="pos-tile-name">{service.name}</span>
          <span className="pos-tile-foot">
            <span className="pos-tile-price">{formatMoneyCents(service.priceCents)}</span>
            {index < MAX_KEYBOARD_TILES ? (
              <kbd className="pos-kbd" aria-hidden="true">
                {index + 1}
              </kbd>
            ) : null}
          </span>
        </button>
      ))}

      {freeformOpen ? (
        <form className="pos-freeform-form" onSubmit={handleFreeformSubmit}>
          <FormField label="Artículo" htmlFor="freeform-name">
            <input
              id="freeform-name"
              autoFocus
              placeholder="Cera para peinar"
              value={name}
              onChange={(event) => setName(event.target.value)}
            />
          </FormField>
          <FormField label="Precio" htmlFor="freeform-price">
            <input
              id="freeform-price"
              inputMode="decimal"
              placeholder="5.00"
              value={priceInput}
              onChange={(event) => setPriceInput(event.target.value)}
            />
          </FormField>
          <button type="submit" className="btn small">
            Agregar
          </button>
          <button
            type="button"
            className="btn small ghost"
            onClick={() => {
              setFreeformOpen(false);
              setError(undefined);
            }}
          >
            Cancelar
          </button>
          {error ? <p className="field-error">{error}</p> : null}
        </form>
      ) : (
        <button type="button" className="pos-tile freeform" onClick={() => setFreeformOpen(true)}>
          + Otro artículo
        </button>
      )}
    </div>
  );
}
